import React, { useState, useEffect, useContext, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import PageLayout from "components/layouts/PageLayout";
import { FieldConfigurationContext } from "../../../lib/context/FieldConfigurationContext";
import { FieldConfigurationProvider } from "../../../lib/context/FieldConfigurationContext";
import TabNavigation from "./TabNavigation";
import { groupRoles, SERVICE_MANAGEMENT_ENABLED } from "./roleUtils";
import { groupUsers } from "./userUtils";
import LicensesDashboard from "./views/LicensesDashboard";
import RolesList from "./views/RolesList";
import RoleForm from "./views/RoleForm";
import RoleDetail from "./views/RoleDetail";
import UsersList from "./views/UsersList";
import UserForm from "./views/UserForm";
import UserDetail from "./views/UserDetail";
import AccessGroupsList from "./views/AccessGroupsList";
import AccessGroupDetail from "./views/AccessGroupDetail";
import AccessGroupForm from "./views/AccessGroupForm";
import APIKeysList from "./views/APIKeysList";
import ApprovalTeamsList from "./views/ApprovalTeamsList";
import ApprovalTeamDetail from "./views/ApprovalTeamDetail";
import ApprovalTeamForm from "./views/ApprovalTeamForm";
import JobAssignments from "./views/JobAssignments";
import JobLocationsList from "./views/JobLocationsList";
import JobLocationDetail from "./views/JobLocationDetail";
import Integrations from "components/admin/Integrations";
import { isProcoreEnabled } from "components/admin/Integrations/featureFlag";
import { CompanyContext } from "../../../lib/context/CompanyContext";
import "./style.css";

function FieldConfigurationContent(props) {
  const history = useHistory();
  const location = useLocation();
  const {
    roles,
    users,
    accessGroups,
    approvalTeams,
    companies,
    adminPermissions,
    fetchAdminPermissions,
    licenses,
    fetchLicenses,
  } = useContext(FieldConfigurationContext);
  const { company } = useContext(CompanyContext);
  const procoreEnabled = isProcoreEnabled(company?.company_user?.email);

  // Maps each tab key to the required administration permission
  const TAB_PERMISSIONS = {
    licenses: "view_licenses",
    roles: "configure_roles",
    users: "configure_users",
    access_groups: "configure_access_groups",
    approval_teams: "configure_approval_groups",
    // API Management shares the Access Groups permission — there is no
    // dedicated admin-permission flag for it, and adding one would require
    // an api_identity role-schema change.
    api_keys: "configure_access_groups",
    // Procore connect/disconnect uses the same check as user management.
    integrations: "configure_users",
    // Job Locations has no dedicated admin-permission flag yet either — it's
    // always shown for company-scoped sessions (see allowedTabs below) until
    // a real permission is added in api_identity.
  };

  // API Management (company_key) has no /user-scoped backend route, so it's
  // only ever available to company-scoped sessions.
  const isCompanyScoped =
    sessionStorage.getItem("field_token_scope") !== "user";

  // Derive which tabs this user is allowed to see
  const allowedTabs = useMemo(() => {
    const perms = adminPermissions.data;
    if (!perms || !perms.enabled) return [];

    // Once licenses have loaded, a company with zero seats across Time,
    // Expense, and Project Management has nothing to configure besides
    // access scoping and API keys — hide the rest of the admin tabs.
    const licensesLoaded = licenses.lastFetched !== null;
    const licenseData = licenses.data;
    const hasNoFieldLicenses =
      licensesLoaded &&
      (licenseData?.time?.limit || 0) === 0 &&
      (licenseData?.expenses?.limit || 0) === 0 &&
      (licenseData?.projectManagement?.limit || 0) === 0 &&
      (!SERVICE_MANAGEMENT_ENABLED ||
        (licenseData?.serviceManagement?.limit || 0) === 0);

    const candidateTabs = hasNoFieldLicenses
      ? isCompanyScoped
        ? ["access_groups", "api_keys"]
        : ["access_groups"]
      : isCompanyScoped
      ? [
          "licenses",
          "roles",
          "users",
          "access_groups",
          "approval_teams",
          "job_locations",
          "api_keys",
          "integrations",
        ]
      : [
          "licenses",
          "roles",
          "users",
          "access_groups",
          "approval_teams",
          "integrations",
        ];
    return candidateTabs.filter(
      (tab) =>
        (tab !== "integrations" || procoreEnabled) &&
        (tab === "job_locations" || perms[TAB_PERMISSIONS[tab]])
    );
  }, [
    adminPermissions.data,
    procoreEnabled,
    isCompanyScoped,
    licenses.data,
    licenses.lastFetched,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check authentication and fetch admin permissions on mount
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      history.replace("/login");
      return;
    }
    fetchAdminPermissions();
    // Needed to determine allowedTabs (see hasNoFieldLicenses above), not
    // just for the Licenses tab's own content.
    fetchLicenses();
  }, [history, fetchAdminPermissions, fetchLicenses]);

  // Once permissions are loaded, verify the user has admin access
  useEffect(() => {
    if (!adminPermissions.loaded || adminPermissions.loading) return;
    if (!adminPermissions.data?.enabled) {
      history.replace("/login");
    }
  }, [
    adminPermissions.loaded,
    adminPermissions.loading,
    adminPermissions.data,
    history,
  ]);

  // Tab state - 'licenses', 'roles', and 'users'
  const [activeTab, setActiveTab] = useState("licenses");

  // State for managing selected items and views
  const [selectedItem, setSelectedItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showJobAssignments, setShowJobAssignments] = useState(false);
  const [capabilityFilter, setCapabilityFilter] = useState(null);
  // Job Locations doesn't lift its data into FieldConfigurationContext (jobs
  // are fetched inside JobLocationsList itself), so its detail view uses its
  // own local selection state instead of the URL-driven selectedItem below.
  const [selectedJob, setSelectedJob] = useState(null);

  useEffect(() => {
    if (activeTab !== "job_locations" && selectedJob) {
      setSelectedJob(null);
    }
  }, [activeTab, selectedJob]);

  // Handle default route - redirect to the first tab the user can access
  useEffect(() => {
    if (
      location.pathname === "/admin/field-configuration" &&
      adminPermissions.loaded &&
      allowedTabs.length > 0
    ) {
      history.replace(`/admin/field-configuration/${allowedTabs[0]}`);
    }
  }, [location.pathname, history, adminPermissions.loaded, allowedTabs]);

  // Parse URL and sync to component state
  useEffect(() => {
    // Parse path parts: /admin/field-configuration/[tab]/[id]
    const pathParts = location.pathname.split("/");
    const urlTab = pathParts[3]; // 'licenses', 'roles', 'users', or undefined
    const urlId = pathParts[4]; // UUID or 'new'

    // Parse query params
    const params = new URLSearchParams(location.search);
    const filterParam = params.get("filter");

    // Determine active tab
    const tab = urlTab || "licenses";

    // Guard: if permissions are loaded and this tab isn't allowed, redirect
    if (
      adminPermissions.loaded &&
      allowedTabs.length > 0 &&
      !allowedTabs.includes(tab)
    ) {
      history.replace(`/admin/field-configuration/${allowedTabs[0]}`);
      return;
    }

    if (tab !== activeTab) {
      setActiveTab(tab);
    }

    // Handle capability filter
    if (filterParam && filterParam !== capabilityFilter) {
      setCapabilityFilter(filterParam);
    } else if (!filterParam && capabilityFilter && tab === "users") {
      // Clear filter if not in URL and we're on users tab
      setCapabilityFilter(null);
    }

    // Handle new form
    if (urlId === "new") {
      if (!showAddForm) {
        setShowAddForm(true);
        setSelectedItem(null);
      }
      if (showJobAssignments) setShowJobAssignments(false);
    }
    // Handle the Job Assignments sub-view under Approval Teams
    else if (tab === "approval_teams" && urlId === "job-assignments") {
      if (!showJobAssignments) setShowJobAssignments(true);
      if (selectedItem) setSelectedItem(null);
      if (showAddForm) setShowAddForm(false);
    }
    // Handle detail view
    else if (urlId && tab !== "licenses") {
      // Find item by UUID
      let item = null;
      if (tab === "roles") {
        const grouped = groupRoles(roles.data, companies.data);
        item = grouped.find(
          (r) => r.uuids?.includes(urlId) || r.uuid === urlId
        );
      } else if (tab === "users") {
        const grouped = groupUsers(users.data, companies.data);
        item = grouped.find(
          (u) => u.uuids?.includes(urlId) || u.uuid === urlId
        );
      } else if (tab === "access_groups") {
        item = accessGroups.data.find((g) => g.uuid === urlId);
      } else if (tab === "approval_teams") {
        item = approvalTeams.data.find((t) => t.uuid === urlId);
      }

      if (item) {
        if (!selectedItem || selectedItem.uuid !== item.uuid) {
          setSelectedItem(item);
          setShowAddForm(false);
        }
        if (showJobAssignments) setShowJobAssignments(false);
      } else {
        // Item not found - redirect to list view
        // Only redirect if data is loaded and item still not found
        if (
          (tab === "roles" && roles.data.length > 0) ||
          (tab === "users" && users.data.length > 0) ||
          (tab === "access_groups" && accessGroups.data.length > 0) ||
          (tab === "approval_teams" && approvalTeams.data.length > 0)
        ) {
          history.replace(`/admin/field-configuration/${tab}`);
        }
      }
    }
    // Handle list view
    else {
      if (selectedItem || showAddForm) {
        setSelectedItem(null);
        setShowAddForm(false);
      }
      if (showJobAssignments) setShowJobAssignments(false);
    }
  }, [
    location.pathname,
    location.search,
    activeTab,
    selectedItem,
    showAddForm,
    showJobAssignments,
    capabilityFilter,
    roles.data,
    users.data,
    accessGroups.data,
    approvalTeams.data,
    companies.data,
    history,
    adminPermissions.loaded,
    allowedTabs,
  ]);

  // Handle tab change and reset state
  const handleTabChange = (tab) => {
    // Job Locations detail is local state, so the URL doesn't change on re-click
    setSelectedJob(null);
    history.push(`/admin/field-configuration/${tab}`);
  };

  // Handle role selection
  const handleRoleSelect = (role) => {
    setSelectedItem(role); // Store full grouped role before URL triggers effect
    history.push(`/admin/field-configuration/roles/${role.uuid}`);
  };

  // Handle add role
  const handleAddRole = () => {
    history.push("/admin/field-configuration/roles/new");
  };

  // Handle user selection
  const handleUserSelect = (user) => {
    history.push(`/admin/field-configuration/users/${user.uuid}`);
  };

  // Handle add user
  const handleAddUser = () => {
    history.push("/admin/field-configuration/users/new");
  };

  // Handle group selection
  const handleGroupSelect = (group) => {
    history.push(`/admin/field-configuration/access_groups/${group.uuid}`);
  };

  // Handle add group
  const handleAddGroup = () => {
    history.push("/admin/field-configuration/access_groups/new");
  };

  // Handle approval team selection
  const handleTeamSelect = (team) => {
    history.push(`/admin/field-configuration/approval_teams/${team.uuid}`);
  };

  // Handle add approval team
  const handleAddTeam = () => {
    history.push("/admin/field-configuration/approval_teams/new");
  };

  // Handle navigation to the Job Assignments sub-view
  const handleJobAssignments = () => {
    history.push("/admin/field-configuration/approval_teams/job-assignments");
  };

  // Handle navigation from license view details
  const handleLicenseViewDetails = (licenseType) => {
    // Map license type to capability filter
    const capabilityMap = {
      time: "time",
      expense: "expense",
      project: "projectManagement",
      service: "serviceManagement",
    };

    const filter = capabilityMap[licenseType];
    history.push(`/admin/field-configuration/users?filter=${filter}`);
  };

  // Handle back navigation from detail/form views
  const handleBack = () => {
    history.push(`/admin/field-configuration/${activeTab}`);
  };

  // Handle clearing capability filter
  const handleClearFilter = () => {
    history.push("/admin/field-configuration/users");
  };

  // Show loading state while permissions are being fetched
  if (!adminPermissions.loaded) {
    return (
      <PageLayout {...props}>
        <div style={{ padding: "48px 24px", textAlign: "center" }}>
          <p>Loading...</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout {...props}>
      <div className="field-configuration">
        {/* Tab Navigation */}
        <TabNavigation
          activeTab={activeTab}
          onTabChange={handleTabChange}
          allowedTabs={allowedTabs}
        />

        {/* Tab Content */}
        <div className="field-configuration__content">
          {activeTab === "licenses" && (
            <LicensesDashboard onViewDetails={handleLicenseViewDetails} />
          )}
          {activeTab === "roles" && !selectedItem && !showAddForm && (
            <RolesList
              onRoleSelect={handleRoleSelect}
              onAddRole={handleAddRole}
            />
          )}
          {activeTab === "roles" && showAddForm && (
            <RoleForm onSave={handleBack} onCancel={handleBack} />
          )}
          {activeTab === "roles" && selectedItem && (
            <RoleDetail
              role={selectedItem}
              onBack={handleBack}
              onUpdate={(updatedRole) => {
                if (updatedRole) {
                  setSelectedItem(updatedRole);
                } else {
                  // No grouped role data — navigate to list (roles refreshed in context)
                  handleBack();
                }
              }}
            />
          )}
          {activeTab === "users" && !selectedItem && !showAddForm && (
            <UsersList
              onUserSelect={handleUserSelect}
              onAddUser={handleAddUser}
              capabilityFilter={capabilityFilter}
              onClearFilter={handleClearFilter}
            />
          )}
          {activeTab === "users" && showAddForm && (
            <UserForm onSave={handleBack} onCancel={handleBack} />
          )}
          {activeTab === "users" && selectedItem && (
            <UserDetail
              user={selectedItem}
              onBack={handleBack}
              onUpdate={(updatedUser) => {
                setSelectedItem(updatedUser);
                // List will auto-refresh via context
              }}
            />
          )}
          {activeTab === "access_groups" && !selectedItem && !showAddForm && (
            <AccessGroupsList
              onGroupSelect={handleGroupSelect}
              onAddGroup={handleAddGroup}
            />
          )}
          {activeTab === "access_groups" && showAddForm && (
            <AccessGroupForm
              onSave={(newGroup) => {
                if (newGroup?.uuid) {
                  history.push(
                    `/admin/field-configuration/access_groups/${newGroup.uuid}`,
                    { justCreated: true }
                  );
                } else {
                  handleBack();
                }
              }}
              onCancel={handleBack}
            />
          )}
          {activeTab === "access_groups" && selectedItem && (
            <AccessGroupDetail
              group={selectedItem}
              onBack={handleBack}
              onUpdate={(updatedGroup) => {
                setSelectedItem(updatedGroup);
              }}
              startEditing={!!location.state?.justCreated}
            />
          )}
          {activeTab === "approval_teams" &&
            !selectedItem &&
            !showAddForm &&
            !showJobAssignments && (
              <ApprovalTeamsList
                onTeamSelect={handleTeamSelect}
                onAddTeam={handleAddTeam}
                onJobAssignments={handleJobAssignments}
              />
            )}
          {activeTab === "approval_teams" && showJobAssignments && (
            <JobAssignments onBack={handleBack} />
          )}
          {activeTab === "approval_teams" && showAddForm && (
            <ApprovalTeamForm
              onSave={(newTeam) => {
                if (newTeam?.uuid) {
                  history.push(
                    `/admin/field-configuration/approval_teams/${newTeam.uuid}`,
                    { justCreated: true }
                  );
                } else {
                  handleBack();
                }
              }}
              onCancel={handleBack}
            />
          )}
          {activeTab === "approval_teams" && selectedItem && (
            <ApprovalTeamDetail
              team={selectedItem}
              onBack={handleBack}
              onUpdate={(updatedTeam) => {
                setSelectedItem(updatedTeam);
              }}
              startEditing={!!location.state?.justCreated}
            />
          )}
          {activeTab === "job_locations" && !selectedJob && (
            <JobLocationsList onJobSelect={setSelectedJob} />
          )}
          {activeTab === "job_locations" && selectedJob && (
            <JobLocationDetail
              job={selectedJob}
              onBack={() => setSelectedJob(null)}
              onUpdate={setSelectedJob}
            />
          )}
          {activeTab === "api_keys" && <APIKeysList />}
          {activeTab === "integrations" && procoreEnabled && (
            <Integrations embedded />
          )}
        </div>
      </div>
    </PageLayout>
  );
}

export default function FieldConfiguration(props) {
  return (
    <FieldConfigurationProvider>
      <FieldConfigurationContent {...props} />
    </FieldConfigurationProvider>
  );
}
